import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  TextField,
  Typography,
  Alert,
} from '@mui/material'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { apiRoot } from '../config'

function ResetPasswordConfirmPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uid, token } = useParams()
  const { register, handleSubmit, formState: { errors }, watch } = useForm<{ new_password: string; re_new_password: string }>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{ success?: boolean; message?: string } | null>(null)

  const password = watch('new_password', '')

  const handleResetPassword = async (data: { new_password: string, re_new_password: string }) => {
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      await axios.post(apiRoot + '/auth/password/reset/confirm/', {
        uid,
        token,
        new_password1: data.new_password,
        new_password2: data.re_new_password,
      })

      setSubmitStatus({
        success: true,
        message: t('passwordResetSuccess'),
      })

      // Redirect to login page after successful reset
      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: t('passwordResetError'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container maxWidth="xs">
      <Box
        component="form"
        sx={{
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleResetPassword)}
      >
        <Typography component="h1" variant="h5">
          {t('setNewPasswordTitle')}
        </Typography>

        {submitStatus && (
          <Alert
            severity={submitStatus.success ? 'success' : 'error'}
            sx={{ width: '100%', mt: 2, mb: 2 }}
          >
            {submitStatus.message}
          </Alert>
        )}

        <TextField
          {...register('new_password', {
            required: t('passwordRequired'),
            minLength: {
              value: 8,
              message: t('passwordMinLength'),
            },
          })}
          margin="normal"
          required
          fullWidth
          name="new_password"
          label={t('newPasswordLabel')}
          type="password"
          id="new_password"
          autoComplete="new-password"
          error={!!errors.new_password}
          helperText={errors.new_password ? String(errors.new_password.message) : ''}
        />

        <TextField
          {...register('re_new_password', {
            required: t('confirmPasswordRequired'),
            validate: (value) =>
              value === password || t('passwordsDontMatch')
          })}
          margin="normal"
          required
          fullWidth
          name="re_new_password"
          label={t('confirmNewPasswordLabel')}
          type="password"
          id="re_new_password"
          autoComplete="new-password"
          error={!!errors.re_new_password}
          helperText={errors.re_new_password ? String(errors.re_new_password.message) : ''}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isSubmitting || submitStatus?.success}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            t('resetPasswordTitle')
          )}
        </Button>
      </Box>
    </Container>
  )
}

export default ResetPasswordConfirmPage
